
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { Skeleton } from '@/components/ui/skeleton';

const Hero = () => {
  const { data: settings, isLoading, isError } = useSiteSettings();

  if (isLoading && !isError) {
    return (
      <section className="relative bg-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center min-h-[75vh] py-20">
            <div className="text-center md:text-left">
              <Skeleton className="h-12 w-3/4 mx-auto md:mx-0" />
              <Skeleton className="h-6 w-full mt-6" />
              <Skeleton className="h-6 w-5/6 mt-2" />
              <div className="mt-10 flex justify-center md:justify-start">
                 <Skeleton className="h-12 w-36" />
              </div>
            </div>
            <div className="relative h-80 md:h-full min-h-[400px]">
              <Skeleton className="absolute inset-0 w-full h-full rounded-lg" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Check if we have a video URL
  const hasVideo = settings?.hero_video_url;

  return (
    <section className="relative bg-secondary overflow-hidden">
      {/* Video Background Section */}
      {hasVideo ? (
        <div className="relative h-[70vh] flex items-center justify-center">
          {/* Video Background */}
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={settings?.hero_video_url} type="video/mp4" />
            {/* Fallback image if video fails to load */}
            <img 
              src={settings?.hero_image_url || "https://images.unsplash.com/photo-1525498128493-380d1990a112?q=80&w=1200&auto=format&fit=crop"}
              alt="A collection of beautiful houseplants"
              className="w-full h-full object-cover"
            />
          </video>
          
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-black/30"></div>
          
          {/* Content Overlay */}
          <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
              {settings?.hero_title || 'Your Home, Greener.'}
            </h1>
            <p className="mt-6 text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto leading-relaxed">
              {settings?.hero_subtitle || "We've reimagined plant care to make it effortless. Discover vibrant, healthy plants delivered from our greenhouse to your home."}
            </p>
            <div className="mt-10 flex justify-center gap-4">
              <Button size="lg" asChild className="bg-white text-black hover:bg-white/90">
                <Link to="/shop">Shop All Plants</Link>
              </Button>
            </div>
          </div>
        </div>
      ) : (
        // Original layout for image-based hero
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center min-h-[75vh] py-20">
            <div className="text-center md:text-left">
              <h1 className="text-4xl md:text-6xl font-bold text-foreground leading-tight">
                {settings?.hero_title || 'Your Home, Greener.'}
              </h1>
              <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-xl mx-auto md:mx-0">
                {settings?.hero_subtitle || "We've reimagined plant care to make it effortless. Discover vibrant, healthy plants delivered from our greenhouse to your home."}
              </p>
              <div className="mt-10 flex justify-center md:justify-start gap-4">
                <Button size="lg" asChild>
                  <Link to="/shop">Shop All Plants</Link>
                </Button>
              </div>
            </div>
            <div className="relative h-80 md:h-full min-h-[400px] rounded-lg overflow-hidden">
               <img 
                  src={settings?.hero_image_url || "https://images.unsplash.com/photo-1525498128493-380d1990a112?q=80&w=1200&auto=format&fit=crop"}
                  alt="A collection of beautiful houseplants"
                  className="absolute inset-0 w-full h-full object-cover"
                  fetchPriority="high"
                />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
